import { useState, useRef, useEffect, useMemo, forwardRef } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaChevronLeft, 
  FaChevronRight, 
  FaCalendarAlt, 
  FaTimes,
  FaCheck
} from "react-icons/fa";
import DropdownPortal from "../common/DropdownPortal";
import { arClass } from "../../utils/useArClass";

// Extracted static texts for different languages
const dateTexts = {
  en: {
    selectDate: "Select date",
    today: "Today",
    selectedDate: "Selected date",
    months: [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ],
    monthsShort: [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ],
    weekdays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    weekdaysFull: [
      "Sunday", "Monday", "Tuesday", "Wednesday", 
      "Thursday", "Friday", "Saturday"
    ],
    clear: "Clear",
    apply: "Apply",
    close: "Close",
    year: "Year"
  },
  fr: {
    selectDate: "Sélectionner une date",
    today: "Aujourd'hui",
    selectedDate: "Date sélectionnée",
    months: [
      "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
      "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
    ],
    monthsShort: [
      "Janv", "Févr", "Mars", "Avr", "Mai", "Juin",
      "Juil", "Août", "Sept", "Oct", "Nov", "Déc"
    ],
    weekdays: ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"],
    weekdaysFull: [
      "Dimanche", "Lundi", "Mardi", "Mercredi", 
      "Jeudi", "Vendredi", "Samedi"
    ],
    clear: "Effacer",
    apply: "Appliquer",
    close: "Fermer",
    year: "Année"
  },
  ar: {
    selectDate: "اختر تاريخًا",
    today: "اليوم",
    selectedDate: "التاريخ المختار",
    months: [
      "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
      "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"
    ],
    monthsShort: [
      "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
      "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"
    ],
    weekdays: ["أحد", "إثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت"],
    weekdaysFull: [
      "الأحد", "الإثنين", "الثلاثاء", "الأربعاء", 
      "الخميس", "الجمعة", "السبت"
    ],
    clear: "مسح",
    apply: "تطبيق",
    close: "إغلاق",
    year: "سنة"
  }
};

const DateInput = forwardRef(({ 
  value = null,
  onChange,
  name,
  className = "",
  mainClassName = "",
  placeholder = null,
  minDate = null,
  maxDate = null,
  disabled = false,
  readOnly = false,
  showTodayButton = true,
  allowClear = true,
  format = "YYYY-MM-DD",
  // React Hook Form compatibility
  register = null,
  registerProp = "date",
  // Additional form props
  onBlur,
  ...props
}, ref) => {


  const defaultLanguage = useSelector((state) => state.settings.defaultLanguage) || "en";
  const darkMode = useSelector((state) => state.settings.darkMode);
  
  
  // Get texts based on language
  const texts = dateTexts[defaultLanguage] || dateTexts.en;
  const isRTL = defaultLanguage === "ar";
  
  // State
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(value ? parseDate(value) : null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [viewMode, setViewMode] = useState("days"); // "days", "months", "years"
  // input for exact year jump
  const [yearInput, setYearInput] = useState("");
  
  const buttonRef = useRef(null);
  
  // Combine refs
  const combinedRef = useRef(null);
  useEffect(() => {
    if (typeof ref === 'function') {
      ref(combinedRef.current);
    } else if (ref) {
      ref.current = combinedRef.current;
    }
  }, [ref]);

  // If the parent passes a `value` prop, keep internal selectedDate in sync
  useEffect(() => {
    if (value) {
      const parsed = parseDate(value);
      setSelectedDate(parsed);
    }
  }, [value]);

  // Keep calendar focused on selected date when it changes
  useEffect(() => {
    if (selectedDate) {
      setCurrentMonth(selectedDate.getMonth());
      setCurrentYear(selectedDate.getFullYear());
    }
  }, [selectedDate]);
  
  // Initialize with register if provided
  // register(name) returns { name, onChange, onBlur, ref }
  const reg = register ? register(registerProp) : null;
  const regOnChange = reg?.onChange;
  const regOnBlur = reg?.onBlur;
  const regRef = reg?.ref;
  const regName = reg?.name;

  // Parse date string to Date object
  function parseDate(dateString) {
  if (!dateString && dateString !== 0) return null;

  // If a Date object is passed, return it (if valid)
  if (dateString instanceof Date) return isNaN(dateString.getTime()) ? null : dateString;

  // If a number (timestamp) is passed
  if (typeof dateString === 'number') {
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? null : d;
  }

  // If an event object was passed (e.g., onChange event), extract value
  if (typeof dateString === 'object' && dateString !== null && 'target' in dateString) {
    dateString = dateString.target.value;
  }

  if (!dateString) return null;

  // Try ISO format first (2025-12-31T00:00:00.000Z)
  const isoMatch = String(dateString).match(/^(\d{4})-(\d{2})-(\d{2})T/);
  if (isoMatch) {
    return new Date(Date.UTC(
      parseInt(isoMatch[1], 10),
      parseInt(isoMatch[2], 10) - 1,
      parseInt(isoMatch[3], 10)
    ));
  }

  // Try different date formats
  const formats = [
    "YYYY-MM-DD",
    "DD/MM/YYYY",
    "MM/DD/YYYY",
    "YYYY/MM/DD"
  ];

  for (const fmt of formats) {
    const parts = String(dateString).split(/[\/\-]/);
    if (parts.length === 3) {
      if (fmt === "YYYY-MM-DD") {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        const date = new Date(year, month, day);
        return isNaN(date.getTime()) ? null : date;
      } else if (fmt === "DD/MM/YYYY") {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        const date = new Date(year, month, day);
        return isNaN(date.getTime()) ? null : date;
      } else if (fmt === "MM/DD/YYYY") {
        const month = parseInt(parts[0], 10) - 1;
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        const date = new Date(year, month, day);
        return isNaN(date.getTime()) ? null : date;
      } else if (fmt === "YYYY/MM/DD") {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        const date = new Date(year, month, day);
        return isNaN(date.getTime()) ? null : date;
      }
    }
  }

  // Fallback to Date constructor for ISO strings
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
}
  
  // Format date to string based on format
  function formatDate(date, formatStr = format) {
    if (!date) return "";
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const monthName = texts.months[date.getMonth()];
    const monthShort = texts.monthsShort[date.getMonth()];
    
    switch (formatStr) {
      case "YYYY-MM-DD":
        return `${year}-${month}-${day}`;
      case "DD/MM/YYYY":
        return `${day}/${month}/${year}`;
      case "MM/DD/YYYY":
        return `${month}/${day}/${year}`;
      case "DD MMM YYYY":
        return `${day} ${monthShort} ${year}`;
      case "MMMM DD, YYYY":
        return `${monthName} ${day}, ${year}`;
      default:
        return `${year}-${month}-${day}`;
    }
  }
  
  // Get locale display string
  function getDisplayDate(date) {
    if (!date) return placeholder || texts.selectDate;
    
    return date.toLocaleDateString(
      defaultLanguage === "ar" ? "ar-MA" :
      defaultLanguage === "fr" ? "fr-FR" :
      "en-US",
      {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }
    );
  }
  
  // Generate calendar days
  const calendarDays = useMemo(() => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay(); // 0 = Sunday
    
    const days = [];
    
    // Previous month days
    const prevMonthLastDay = new Date(currentYear, currentMonth, 0).getDate();
    for (let i = 0; i < startingDay; i++) {
      const day = prevMonthLastDay - startingDay + i + 1;
      const date = new Date(currentYear, currentMonth - 1, day);
      days.push({
        date,
        day,
        isCurrentMonth: false,
        isToday: isToday(date),
        isSelected: selectedDate && isSameDay(date, selectedDate),
        isDisabled: isDateDisabled(date)
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(currentYear, currentMonth, i);
      days.push({
        date,
        day: i,
        isCurrentMonth: true,
        isToday: isToday(date),
        isSelected: selectedDate && isSameDay(date, selectedDate),
        isDisabled: isDateDisabled(date)
      });
    }
    
    // Next month days (fill up to 6 weeks)
    const totalCells = 42; // 6 weeks * 7 days
    const nextMonthDays = totalCells - days.length;
    for (let i = 1; i <= nextMonthDays; i++) {
      const date = new Date(currentYear, currentMonth + 1, i);
      days.push({
        date,
        day: i,
        isCurrentMonth: false,
        isToday: isToday(date),
        isSelected: selectedDate && isSameDay(date, selectedDate),
        isDisabled: isDateDisabled(date)
      });
    }
    
    return days;
  }, [currentMonth, currentYear, selectedDate, minDate, maxDate]);
  
  // Check if date is today
  function isToday(date) {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  }
  
  // Check if two dates are the same day
  function isSameDay(date1, date2) {
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear();
  }
  
  // Check if date is disabled
  function isDateDisabled(date) {
    if (minDate && date < parseDate(minDate)) return true;
    if (maxDate && date > parseDate(maxDate)) return true;
    return false;
  }
  
  // Navigation functions
  const goToPreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };
  
  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // navigate decades when in years view
  const goToPreviousDecade = () => {
    setCurrentYear((y) => y - 10);
  };
  const goToNextDecade = () => {
    setCurrentYear((y) => y + 10);
  };

  // jump to a specific year from input
  const applyYearInput = () => {
    const y = parseInt(yearInput, 10);
    if (!isNaN(y) && y > 0 && y < 10000) {
      setCurrentYear(y);
      setViewMode("days");
      setYearInput("");
    }
  };
  const handleYearKeyDown = (e) => {
    if (e.key === "Enter") applyYearInput();
  };
  
  const goToToday = () => {
    const today = new Date();
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
    handleDateSelect(today);
  };
  
  const handleDateSelect = (date) => {
    if (isDateDisabled(date)) return;

    // ensure calendar shows the correct month/year immediately
    setCurrentMonth(date.getMonth());
    setCurrentYear(date.getFullYear());

    setSelectedDate(date);
    const formattedDate = formatDate(date);

    // Trigger react-hook-form and external onChange/onBlur if provided
    if (regOnChange) {
      regOnChange({ target: { name: regName || name, value: formattedDate, type: 'date' } });
    }

    if (onChange && typeof onChange === "function") {
      const event = {
        target: {
          name,
          value: formattedDate,
          type: "date"
        }
      };
      onChange(event);
    }

    // Trigger onBlur if provided
    if (onBlur && typeof onBlur === "function") {
      onBlur({ target: { name, value: formattedDate } });
    }

    // Close dropdown if not in multi-select mode
    setIsOpen(false);
  };
  
  const clearDate = (e) => {
    if (e) e.stopPropagation();
    
    setSelectedDate(null);
    const formattedDate = "";

    // inform react-hook-form
    if (regOnChange) {
      regOnChange({ target: { name: regName || name, value: formattedDate, type: 'date' } });
    }
    
    // Trigger onChange if provided
    if (onChange && typeof onChange === "function") {
      const event = {
        target: {
          name,
          value: formattedDate,
          type: "date"
        }
      };
      onChange(event);
    }
    
    // Trigger onBlur if provided
    if (onBlur && typeof onBlur === "function") {
      onBlur({ target: { name, value: formattedDate } });
    }
  };
  
  // Generate years for year view (10-year range)
  const yearRange = useMemo(() => {
    const startYear = Math.floor(currentYear / 10) * 10 - 1;
    const years = [];
    for (let i = 0; i < 12; i++) {
      years.push(startYear + i);
    }
    return years;
  }, [currentYear]);
  
  // Get color scheme based on dark mode and sidebar style
  const getColorScheme = () => {
    const entry = {
      light: {
        bg: "!bg-white text-gray-900",
        color: "!text-gray-900",
        subBg: "!bg-gray-50/90 !backdrop-blur-sm",
        border: "!border-gray-300",
        hover: "hover:!bg-gray-200",
        active: "!bg-gray-200 !text-gray-900 !border-gray-400 !shadow",
        iconBg: "!bg-gray-300",
        glow: "!shadow-lg !shadow-gray-300/20",
      },
      dark: {
        bg: "!bg-gray-600 !text-gray-50",
        color: "!text-gray-50",
        subBg: "!bg-gray-700/90 !backdrop-blur-sm",
        border: "!border-gray-300",
        hover: "hover:!bg-gray-800",
        active: "!bg-gray-600 !text-white !border-gray-500 shadow",
        iconBg: "!bg-gray-600",
        glow: "!shadow-lg !shadow-gray-700/30",
      },
    };

    return darkMode ? entry.dark : entry.light;
  };
  
  const colors = getColorScheme();
  
  // RTL styling
  const rtlClasses = isRTL ? "rtl text-right"+mainClassName : mainClassName;
  const flexDirectionClass = isRTL ? "flex-row-reverse" : "";
  const textAlignClass = isRTL ? "text-right" : "text-left";
  
  // Keyboard event handling
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'Escape':
          setIsOpen(false);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (viewMode === "days") goToPreviousMonth();
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (viewMode === "days") goToNextMonth();
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, viewMode]);
  
  // Hidden input for React Hook Form integration
  const hiddenInputProps = {
    name: regName || name,
    value: selectedDate ? formatDate(selectedDate) : "",
    onChange: (e) => {
      // support Date objects, timestamps, plain strings or event objects
      const raw = e && e.target ? e.target.value : e;
      const date = raw instanceof Date ? raw : parseDate(raw);
      if (date) {
        setSelectedDate(date);
        setCurrentMonth(date.getMonth());
        setCurrentYear(date.getFullYear());
      } else {
        setSelectedDate(null);
      }

      if (regOnChange) {
        // normalize to formatted string
        regOnChange({ target: { name: regName || name, value: date ? formatDate(date) : "", type: 'date' } });
      }
    },
    onBlur: (e) => {
      if (onBlur && typeof onBlur === "function") onBlur(e);
      if (regOnBlur) regOnBlur(e);
    },
    ...props
  };

  return (
    <div className={`relative w-full ${rtlClasses}`}>
      {/* Hidden input for form submission */}
      <input
        type="hidden"
        {...hiddenInputProps}
        ref={(el) => {
          combinedRef.current = el;
          if (typeof regRef === 'function') regRef(el);
          else if (regRef && 'current' in regRef) regRef.current = el;
        }}
      />
      
      <div
        type="button"
        ref={buttonRef}
        onClick={() => !disabled && !readOnly && setIsOpen(!isOpen)}
        disabled={disabled}
        className={arClass(`w-full hover:!transform-none flex items-center justify-between gap-3 p-3 rounded-md text-sm font-medium transition-all duration-300 max-sm:text-xs max-sm:p-2 border ${
          darkMode 
            ? "bg-gray-700 text-gray-100 border-gray-600 hover:bg-gray-600" 
            : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50"
        } ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${className} `)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) {
            if (onBlur && typeof onBlur === "function") {
              onBlur({
                target: {
                  name,
                  value: selectedDate ? formatDate(selectedDate) : ""
                }
              });
            }
          }
        }}
      >
        <div className={`flex items-center gap-2 ${flexDirectionClass}`}>
          <FaCalendarAlt className={darkMode ? "text-gray-400" : "text-gray-500"} />
          <span className={`max-sm:text-xs ${textAlignClass}`}>
            {selectedDate ? getDisplayDate(selectedDate) : (placeholder || texts.selectDate)}
          </span>
        </div>
        <div className={`flex items-center gap-2 ${flexDirectionClass}`}>
          {allowClear && selectedDate && (
            <button
              type="button"
              onClick={clearDate}
              className={`p-1 rounded ${darkMode ? "hover:bg-gray-600" : "hover:bg-gray-200"}`}
              title={texts.clear}
            >
              <FaTimes className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
      
      <AnimatePresence>
        {isOpen && !readOnly && (
          <DropdownPortal
            anchorRef={buttonRef}
            onClose={() => setIsOpen(false)}
            className={`${colors.subBg} border ${colors.border} shadow-lg rounded-md p-4 ${textAlignClass} w-full`}
            position="bottom"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full"
            >
              {/* Calendar Header */}
              <div className={`flex items-center justify-between mb-4 ${flexDirectionClass}`}>
                <button
                  type="button"
                  onClick={goToPreviousMonth}
                  className={`p-2 rounded ${darkMode ? "hover:bg-gray-600 text-white" : "hover:bg-gray-200"}`}
                  title={isRTL ? "الشهر التالي" : "Previous month"}
                >
                  <FaChevronLeft className={isRTL ? "rotate-180" : ""} />
                </button>
                
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setViewMode(viewMode === "months" ? "days" : "months")}
                    className={`px-3 py-1 rounded font-medium max-sm:text-xs max-sm:p-2 ${
                      darkMode ? "hover:bg-gray-600 text-white" : "hover:bg-gray-200"
                    }`}
                  >
                    {texts.monthsShort[currentMonth]}
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode(viewMode === "years" ? "days" : "years")}
                    className={`px-3 py-1 rounded font-medium max-sm:text-xs max-sm:p-2 ${
                      darkMode ? "hover:bg-gray-600 text-white" : "hover:bg-gray-200"
                    }`}
                  >
                    {currentYear}
                  </button>
                </div>
                
                <button
                  type="button"
                  onClick={goToNextMonth}
                  className={`p-2 rounded ${darkMode ? "hover:bg-gray-600 text-white" : "hover:bg-gray-200"}`}
                  title={isRTL ? "الشهر السابق" : "Next month"}
                >
                  <FaChevronRight className={isRTL ? "rotate-180" : ""} />
                </button>
              </div>
              
              {/* Month/Year View */}
              {viewMode === "months" && (
                <div className="grid grid-cols-3 gap-2 mb-4 ">
                  {texts.months.map((month, index) => (
                    <button
                      key={month}
                      type="button"
                      onClick={() => {
                        setCurrentMonth(index);
                        setViewMode("days");
                      }}
                      className={`p-1.5 rounded text-sm max-sm:text-xs max-sm:p-1 ${
                        currentMonth === index
                          ? darkMode
                            ? "bg-blue-600 text-white"
                            : "bg-blue-500 text-white"
                          : darkMode
                          ? "hover:bg-gray-600 text-white"
                          : "hover:bg-gray-200"
                      }`}
                    >
                      {texts.monthsShort[index]}
                    </button>
                  ))}
                </div>
              )}
              
              {viewMode === "years" && (
                <div className="mb-4">
                  <div className={`flex flex-col items-center justify-between mb-2 gap-2 ${flexDirectionClass}`}>
                    <div className={`flex items-center gap-2 w-full ${flexDirectionClass}`}>
                      <input
                        type="number"
                        inputMode="numeric"
                        placeholder={
                          defaultLanguage === "ar" ? "السنة" :
                          defaultLanguage === "fr" ? "Année" :
                          "Year"
                        }
                        value={yearInput}
                        onChange={(e) => setYearInput(e.target.value)}
                        onKeyDown={handleYearKeyDown}
                        className={arClass(`px-2 py-1 rounded text-xs border flex-1 ${darkMode ? "bg-gray-700 text-white border-gray-600" : "bg-white text-gray-800 border-gray-300"}`)}
                      />
                      <button
                        type="button"
                        onClick={applyYearInput}
                        className={`px-3 py-1 rounded text-xs flex-1 ${darkMode ? "bg-blue-600 text-white hover:bg-blue-500" : "bg-blue-500 text-white hover:bg-blue-600"}`}
                      >
                        {
                          defaultLanguage === "ar" ? "تطبيق" :
                          defaultLanguage === "fr" ? "Appliquer" :
                          "Apply"
                        }
                      </button>
                    </div>
                    <div className="flex items-center gap-2 w-full">
                      <button
                        type="button"
                        onClick={goToPreviousDecade}
                        className={`text-xs p-2 rounded ${darkMode ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-white hover:bg-gray-100 text-gray-800"}`}
                        title={isRTL ? "Previous decade" : "Previous decade"}
                      >
                        <FaChevronLeft />
                      </button>

                      <div className={`px-3 py-1 rounded text-xs font-medium w-full text-center ${darkMode ? "bg-gray-800 text-gray-200" : "bg-gray-100 text-gray-800"}`}>
                        {`${yearRange[0]} - ${yearRange[yearRange.length - 1]}`}
                      </div>

                      <button
                        type="button"
                        onClick={goToNextDecade}
                        className={`text-xs p-2 rounded ${darkMode ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-white hover:bg-gray-100 text-gray-800"}`}
                        title={isRTL ? "Next decade" : "Next decade"}
                      >
                        <FaChevronRight />
                      </button>
                    </div>

                    
                  </div>

                  <div className="grid grid-cols-4 gap-2">
                    {yearRange.map((year) => (
                      <button
                        key={year}
                        type="button"
                        onClick={() => {
                          setCurrentYear(year);
                          setViewMode("days");
                        }}
                        className={`p-1.5 rounded text-sm max-sm:text-xs max-sm:p-1 ${currentYear === year ? (darkMode ? "bg-blue-600 text-white" : "bg-blue-500 text-white") : (darkMode ? "hover:bg-gray-600 text-gray-100" : "hover:bg-gray-200 text-gray-800")}`}
                      >
                        {year}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Days View */}
              {viewMode === "days" && (
                <>
                  {/* Weekdays Header */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {texts.weekdays.map((day) => (
                      <div
                        key={day}
                        className={`text-center text-xs font-medium p-1 ${
                          darkMode ? "text-gray-200 " : "text-gray-500"
                        }`}
                      >
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  {/* Calendar Days */}
                  <div className="grid grid-cols-7 gap-1">
                    {calendarDays.map((day, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleDateSelect(day.date)}
                        disabled={day.isDisabled}
                        className={`
                          h-8  rounded text-sm flex items-center justify-center max-sm:text-xs max-sm:p-2 max-sm:h-6 
                          ${day.isCurrentMonth ? "font-medium" : "opacity-40"}
                          ${day.isToday 
                            ? darkMode 
                              ? "border border-blue-500" 
                              : "border border-blue-400"
                            : ""
                          }
                          ${day.isSelected
                            ? darkMode
                              ? "bg-blue-600 text-white"
                              : "bg-blue-500 text-white"
                            : day.isDisabled
                            ? darkMode
                              ? "text-gray-600 cursor-not-allowed"
                              : "text-gray-400 cursor-not-allowed"
                            : darkMode
                            ? "hover:bg-gray-600 text-white"
                            : "hover:bg-gray-200"
                          }
                        `}
                        title={day.isDisabled ? "Not available" : ""}
                      >
                        {day.day}
                      </button>
                    ))}
                  </div>
                </>
              )}
              
              {/* Footer */}
              <div className={`flex items-center justify-between mt-4 pt-4 border-t ${darkMode ? "border-gray-600" : "border-gray-200"} ${flexDirectionClass}`}>
                {showTodayButton && (
                  <button
                    type="button"
                    onClick={goToToday}
                    className={`px-3 py-1 rounded text-sm max-sm:text-xs max-sm:p-2 ${
                      darkMode
                        ? "bg-gray-600 hover:bg-gray-500 text-white"
                        : "bg-gray-200 hover:bg-gray-300"
                    }`}
                  >
                    {texts.today}
                  </button>
                )}
                
                <div className={`flex items-center gap-2 ${flexDirectionClass}`}>
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className={`px-3 py-1 rounded text-sm max-sm:text-xs max-sm:p-2 ${
                      darkMode
                        ? "bg-gray-600 hover:bg-gray-500 text-white"
                        : "bg-gray-200 hover:bg-gray-300"
                    }`}
                  >
                    {texts.close}
                  </button>
                  {selectedDate && (
                    <button
                      type="button"
                      onClick={() => setIsOpen(false)}
                      className={`px-3 py-1 rounded text-sm max-sm:text-xs max-sm:p-2 ${
                        darkMode
                          ? "bg-blue-600 hover:bg-blue-500 text-white"
                          : "bg-blue-500 hover:bg-blue-600 text-white"
                      }`}
                    >
                      {texts.apply}
                    </button>
                  )}
                </div>
              </div>
              
              {/* Selected Date Display */}
              {selectedDate && (
                <div className={`mt-3 p-3 rounded ${darkMode ? "bg-gray-600" : "bg-gray-100"}`}>
                  <div className={arClass(`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`)}>
                    {texts.selectedDate}:
                  </div>
                  <div className={`font-medium text-xs  ${darkMode ? "text-white" : "text-gray-800"}`}>
                    {getDisplayDate(selectedDate)}
                  </div>
                 
                </div>
              )}
            </motion.div>
          </DropdownPortal>
        )}
      </AnimatePresence>
    </div>
  );
});

// Add display name for better debugging
DateInput.displayName = "DateInput";

export default DateInput;